import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { PAGE_HEADER_HEIGHT } from '../constants/layout';
import { RetroCreator } from '../components/RetroCreator';
import { RetroViewer } from '../components/RetroViewer';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { supabase } from '../utils/supabase';
import { format } from 'date-fns';
import { IoArrowBack, IoSaveOutline } from 'react-icons/io5';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../utils/logger';
import { useBannerAutoRead } from '../hooks/useBannerAutoRead';
import useTouchScroll from '../hooks/useTouchScroll';

const logger = createLogger({ module: 'Retrospectives', level: 'info' });

interface RetroData {
  id: string;
  title: string;
  description: string;
  framework_type: string;
  start_date: string;
  end_date: string;
  created_at: string;
}

interface RetrospectivesContentProps {
  isCreating: boolean;
  onClose: () => void;
  onCreateRetro: () => void;
  initialRetrospectives?: RetroData[];
}

const RetroCard: React.FC<{ retro: RetroData }> = ({ retro }) => {
  const { trackFeatureUsage } = useBusinessMetrics();
  
  const formatFrameworkType = (type: string) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleRetroCardClick = () => {
    // Track when a user clicks on a retrospective card
    trackFeatureUsage('retrospective_card', 'clicked', {
      retro_id: retro.id,
      retro_title: retro.title
    });
  };

  return (
    <Link
      to={`${retro.id}`}
      className="block p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
      onClick={handleRetroCardClick}
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{retro.title}</h3>
        <span className="px-2.5 py-0.5 text-sm rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {formatFrameworkType(retro.framework_type)}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">{retro.description}</p>
      <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
        <span>Created {format(new Date(retro.created_at), 'MMM d, yyyy')}</span>
        <span>{format(new Date(retro.start_date), 'MMM d')} - {format(new Date(retro.end_date), 'MMM d, yyyy')}</span>
      </div>
    </Link>
  );
};

const RetrospectivesContent: React.FC<RetrospectivesContentProps> = ({
  isCreating,
  onClose,
  onCreateRetro,
  initialRetrospectives = []
}) => {
  const { darkMode } = useDarkMode();
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  const [retros, setRetros] = useState<RetroData[]>(initialRetrospectives);
  const [loading, setLoading] = useState<boolean>(initialRetrospectives.length === 0);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [retroListJourneyId, setRetroListJourneyId] = useState<string | null>(null);

  useEffect(() => {
    // Start a journey for viewing the retrospectives list
    const journeyId = `retro_list_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setRetroListJourneyId(journeyId);
    
    // Track page view
    trackFeatureUsage('retrospectives_list', 'viewed');
    
    trackUserJourney('retrospectives_list_started', {
      journey_id: journeyId,
      entry_point: 'direct'
    });
    
    return () => {
      // If the journey is still active when component unmounts, complete it
      if (retroListJourneyId) {
        trackUserJourney('retrospectives_list_completed', {
          journey_id: retroListJourneyId,
          outcome: 'journey_complete'
        });
      }
    };
  }, []);

  useEffect(() => {
    const loadRetros = async () => {
      const startTime = performance.now();
      try {
        setLoading(true);
        
        // Track the start of data loading
        trackUserJourney('retrospectives_loading_started', {
          journey_id: retroListJourneyId
        });
        
        const { data, error } = await supabase
          .from('retrospectives')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setRetros(data || []);
        
        // Track successful data loading
        trackUserJourney('retrospectives_loaded', {
          journey_id: retroListJourneyId,
          count: data?.length || 0
        });
        
        // Track performance metrics
        trackFeatureUsage('retrospectives_fetch', 'completed', {
          journey_id: retroListJourneyId,
          duration_ms: Math.round(performance.now() - startTime),
          success: true,
          count: data?.length || 0
        });
      } catch (error) {
        console.error('Error loading retrospectives:', error);
        
        // Track error in journey
        trackUserJourney('retrospectives_loading_failed', {
          journey_id: retroListJourneyId,
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
        
        // Track performance metrics with error
        trackFeatureUsage('retrospectives_fetch', 'failed', {
          journey_id: retroListJourneyId,
          duration_ms: Math.round(performance.now() - startTime),
          success: false,
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadRetros();
  }, [retroListJourneyId]);

  if (isCreating) {
    return <RetroCreator onClose={onClose} />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (retros.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          You haven't created any retrospectives yet.
        </p>
        <button
          onClick={onCreateRetro}
          className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Create your first retrospective
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {retros.map(retro => (
        <RetroCard key={retro.id} retro={retro} />
      ))}
    </div>
  );
};

// Add a RetroViewer wrapper component to handle analytics
interface RetroViewerWithAnalyticsProps {
  isEditMode: boolean;
  onEditModeChange: (newEditMode: boolean) => void;
}

const RetroViewerWithAnalytics: React.FC<RetroViewerWithAnalyticsProps> = ({ isEditMode, onEditModeChange }) => {
  const { retroId } = useParams<{ retroId: string }>();
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  const [retroViewJourneyId, setRetroViewJourneyId] = useState<string | null>(null);
  const retroViewerRef = useRef<{ handleEditModeToggle: () => void; handleSaveChanges: () => void }>(null);
  const location = useLocation();
  
  useEffect(() => {
    if (!retroId) return;
    
    // Start a journey for viewing a specific retrospective
    const journeyId = `retro_view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setRetroViewJourneyId(journeyId);
    
    // Track retrospective view
    trackFeatureUsage('retrospective_detail', 'viewed', {
      journey_id: journeyId,
      retro_id: retroId
    });
    
    trackUserJourney('retrospective_view_started', {
      journey_id: journeyId,
      retro_id: retroId
    });
    
    // Track performance
    trackFeatureUsage('retrospective_performance', 'measured', {
      journey_id: journeyId,
      route: `/retrospectives/${retroId}`,
      load_time_ms: Math.round(performance.now())
    });
    
    return () => {
      // If the journey is still active when component unmounts, complete it
      if (retroViewJourneyId) {
        trackUserJourney('retrospective_view_completed', {
          journey_id: retroViewJourneyId,
          outcome: 'success'
        });
      }
    };
  }, [retroId]);
  
  // Track edit mode changes
  useEffect(() => {
    if (isEditMode && retroViewJourneyId) {
      trackUserJourney('retrospective_edit_mode_entered', {
        journey_id: retroViewJourneyId,
        retro_id: retroId
      });
    }
  }, [isEditMode, retroId, retroViewJourneyId]);
  
  return (
    <RetroViewer 
      ref={retroViewerRef}
      isEditMode={isEditMode}
      onEditModeChange={(newEditMode) => {
        onEditModeChange(newEditMode);
      }}
    />
  );
};

/**
 * Interface for the Retrospectives page props
 * Allows passing server-side rendered data to the component
 */
interface RetrospectivesPageProps {
  initialRetrospectives?: RetroData[];
  initialFeatureFlags?: {
    enable_retrospectives?: boolean;
    [key: string]: any;
  };
}

/**
 * Main component for the Retrospectives page
 * Handles listing, creating, and viewing retrospectives
 */
const RetrospectivesPage: React.FC<RetrospectivesPageProps> = ({
  initialRetrospectives = [],
  initialFeatureFlags = { enable_retrospectives: true }
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();
  
  // Auto-mark banner notifications as read when landing on retrospectives page
  useBannerAutoRead();
  // Use initialFeatureFlags if provided, otherwise use the hook
  const ssrFeatureEnabled = initialFeatureFlags?.enable_retrospectives;
  const { isEnabled: hookFeatureEnabled, isLoading: featureLoading } = useFeatureFlag('enable_retrospectives');
  // Prefer SSR flag if available (and not loading), otherwise use hook result
  const isRetroEnabled = ssrFeatureEnabled !== undefined ? ssrFeatureEnabled : hookFeatureEnabled;
  
  const location = useLocation();
  const retroViewerRef = useRef<{ handleEditModeToggle: () => void; handleSaveChanges: () => void }>(null);
  const [pageJourneyId, setPageJourneyId] = useState<string | null>(null);
  const [creationJourneyId, setCreationJourneyId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Apply touch scroll handling
  useTouchScroll(scrollContainerRef);

  // Check if we're viewing a retro (URL has an ID)
  const isViewingRetro = location.pathname.split('/').length > 2;

  // Track page view and start journey
  useEffect(() => {
    // Start a journey for the retrospectives page
    const journeyId = `retro_page_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setPageJourneyId(journeyId);
    
    // Track page view
    trackFeatureUsage('retrospectives_page', 'viewed');
    
    trackUserJourney('retrospectives_page_started', {
      journey_id: journeyId,
      entry_point: 'direct'
    });
    
    // Track performance
    trackFeatureUsage('page_performance', 'measured', {
      journey_id: journeyId,
      route: '/retrospectives',
      load_time_ms: Math.round(performance.now())
    });
    
    return () => {
      // If the journey is still active when component unmounts, complete it
      if (pageJourneyId) {
        trackUserJourney('retrospectives_page_completed', {
          journey_id: pageJourneyId,
          outcome: 'journey_complete'
        });
      }
      
      // If a creation journey is active, abandon it
      if (creationJourneyId) {
        trackUserJourney('retrospective_creation_abandoned', {
          journey_id: creationJourneyId,
          reason: 'page_exit'
        });
      }
    };
  }, []);

  // Check for edit=true query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('edit') === 'true') {
      setIsEditMode(true);
      
      // Track edit mode from URL parameter
      trackFeatureUsage('retrospective_edit_from_url', 'viewed');
    }
  }, [location.search]);

  // Load retros if not provided via SSR
  useEffect(() => {
    if (initialRetrospectives.length === 0) {
      // No need to fetch data here - we're delegating that to the content component
      logger.debug('No SSR retrospectives data, will fetch on client');
    } else {
      logger.debug(`Using ${initialRetrospectives.length} SSR retrospectives`);
    }
  }, [initialRetrospectives]);

  const handleCreateRetro = () => {
    // Start a journey for retrospective creation
    const journeyId = `retro_creation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setCreationJourneyId(journeyId);
    
    // Track create button click
    trackFeatureUsage('create_retrospective_button', 'clicked');
    
    trackUserJourney('retrospective_creation_started', {
      journey_id: journeyId,
      step_type: 'intermediate'
    });
    
    setIsCreating(true);
  };

  const handleCloseCreator = () => {
    // Track cancellation
    if (creationJourneyId) {
      trackFeatureUsage('cancel_retrospective_creation', 'clicked');
      
      trackUserJourney('retrospective_creation_abandoned', {
        journey_id: creationJourneyId,
        reason: 'user_abandoned'
      });
      setCreationJourneyId(null);
    }
    
    setIsCreating(false);
  };

  const handleEditModeToggle = () => {
    // Track edit mode toggle
    trackFeatureUsage(isEditMode ? 'exit_edit_mode' : 'enter_edit_mode', 'clicked');
    
    retroViewerRef.current?.handleEditModeToggle();
  };

  const handleSaveChanges = () => {
    // Track save changes
    trackFeatureUsage('save_retrospective_changes', 'clicked');
    
    // Note: We would ideally track the completion after the save is successful,
    // but for basic tracking we'll just track the click for now
    
    retroViewerRef.current?.handleSaveChanges();
  };

  if (featureLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isRetroEnabled) {
    // Track feature flag disabled view
    trackFeatureUsage('retrospectives_feature_disabled', 'viewed');
    
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          This feature is not available yet.
        </p>
      </div>
    );
  }

  const renderHeaderButton = () => {
    if (isViewingRetro) {
      if (isEditMode) {
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={handleEditModeToggle}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-700"
            >
              <div className="flex items-center gap-2">
                <IoArrowBack className="text-lg" />
                Cancel
              </div>
            </button>
            <button
              onClick={handleSaveChanges}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              <div className="flex items-center gap-2">
                <IoSaveOutline className="text-lg" />
                Save Changes
              </div>
            </button>
          </div>
        );
      } else {
        return (
          <button
            onClick={handleEditModeToggle}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            Edit
          </button>
        );
      }
    } else {
      return (
        <button
          onClick={handleCreateRetro}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
        >
          New Retro
        </button>
      );
    }
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Retrospectives"
        primaryButton={renderHeaderButton()}
      />
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
        style={{ paddingTop: `${PAGE_HEADER_HEIGHT}px` }}
      >
        <div className="p-4">
          <ErrorBoundary>
            <Routes>
              <Route 
                path="/" 
                element={
                  <RetrospectivesContent
                    isCreating={isCreating}
                    onClose={handleCloseCreator}
                    onCreateRetro={handleCreateRetro}
                    initialRetrospectives={initialRetrospectives}
                  />
                } 
              />
              <Route 
                path="/:retroId" 
                element={
                  <RetroViewerWithAnalytics 
                    isEditMode={isEditMode}
                    onEditModeChange={(newEditMode) => {
                      setIsEditMode(newEditMode);
                    }}
                  />
                } 
              />
            </Routes>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default RetrospectivesPage; 